import { useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Upload, FileSpreadsheet, CheckCircle, XCircle, AlertTriangle, Loader2, Download } from 'lucide-react';
import { toast } from 'sonner';

/**
 * GapAssessmentImport Component
 * 
 * Allows users to upload an Excel file with gap assessment data
 * to automatically update control statuses.
 * 
 * Expected Excel format:
 * - Column "Control": Control ID (e.g., "A.5.1", "CC1.1.1")
 * - Column "Status": Status value ("Open" = Non-compliant, anything else = Compliant)
 */
export default function GapAssessmentImport({ 
  framework, 
  frameworkDisplayName, 
  onImportComplete,
  viewTenantId 
}) {
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith('.xlsx') || droppedFile.name.endsWith('.xls')) {
        setFile(droppedFile);
        setResult(null);
      } else {
        toast.error('Please upload an Excel file (.xlsx or .xls)');
      }
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('framework', framework);

      const response = await axios.post(
        `${API}/controls/import/gap-assessment`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setResult(response.data);
      
      if (response.data.updated_controls > 0) {
        toast.success(`Successfully updated ${response.data.updated_controls} controls`);
        if (onImportComplete) {
          onImportComplete();
        }
      } else if (response.data.not_found_count > 0) {
        toast.warning(`No controls were updated. ${response.data.not_found_count} controls not found in the system.`);
      }
    } catch (error) {
      console.error('Import error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to import gap assessment';
      toast.error(errorMessage);
      setResult({
        error: true,
        message: errorMessage
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setFile(null);
    setResult(null);
  };

  const downloadTemplate = () => {
    // Create a simple CSV template that users can open in Excel
    const csvContent = "Control,Status\nA.5.1,Closed\nA.5.2,Open\nA.5.3,Closed";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gap_assessment_template_${framework}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        className="border-slate-300 text-slate-700 hover:bg-slate-50"
        data-testid="import-gap-assessment-btn"
      >
        <Upload className="w-4 h-4 mr-2" />
        Import Gap Assessment
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-slate-600" />
              Import Gap Assessment
            </DialogTitle>
            <DialogDescription>
              Upload an Excel file to update {frameworkDisplayName || framework} control statuses
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm">
              <p className="font-medium text-blue-900 mb-2">Expected Excel Format:</p>
              <ul className="text-blue-800 space-y-1">
                <li>• <strong>Control</strong> column: Control ID (e.g., "A.5.1", "CC1.1.1")</li>
                <li>• <strong>Status</strong> column: "Open" = Non-compliant, anything else = Compliant</li>
              </ul>
              <button 
                onClick={downloadTemplate}
                className="text-blue-600 hover:text-blue-800 underline mt-2 text-xs"
              >
                Download sample template
              </button>
            </div>

            {/* File Upload Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : file 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-slate-300 hover:border-slate-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <FileSpreadsheet className="w-10 h-10 text-green-600" />
                  <p className="text-sm font-medium text-green-900">{file.name}</p>
                  <p className="text-xs text-green-600">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                  <button
                    onClick={() => setFile(null)}
                    className="text-xs text-red-600 hover:text-red-800 underline mt-1"
                  >
                    Remove file
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-10 h-10 text-slate-400" />
                  <p className="text-sm text-slate-600">
                    Drag and drop your Excel file here, or
                  </p>
                  <label className="cursor-pointer">
                    <span className="text-sm text-blue-600 hover:text-blue-800 underline">
                      browse to select
                    </span>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                  <p className="text-xs text-slate-500 mt-1">
                    Supports .xlsx and .xls files
                  </p>
                </div>
              )}
            </div>

            {/* Results */}
            {result && !result.error && (
              <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Import Complete
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-white rounded p-2 border">
                    <p className="text-slate-500 text-xs">Total Rows</p>
                    <p className="text-lg font-semibold text-slate-900">{result.total_rows}</p>
                  </div>
                  <div className="bg-white rounded p-2 border">
                    <p className="text-slate-500 text-xs">Matched</p>
                    <p className="text-lg font-semibold text-green-600">{result.matched_controls}</p>
                  </div>
                  <div className="bg-white rounded p-2 border">
                    <p className="text-slate-500 text-xs">Updated</p>
                    <p className="text-lg font-semibold text-blue-600">{result.updated_controls}</p>
                  </div>
                  <div className="bg-white rounded p-2 border">
                    <p className="text-slate-500 text-xs">Not Found</p>
                    <p className="text-lg font-semibold text-amber-600">{result.not_found_count}</p>
                  </div>
                </div>
                
                {result.not_found_controls && result.not_found_controls.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <p className="text-xs text-amber-700 font-medium mb-1 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Controls not found in system:
                    </p>
                    <p className="text-xs text-slate-600 break-words">
                      {result.not_found_controls.slice(0, 10).join(', ')}
                      {result.not_found_controls.length > 10 && ` ... and ${result.not_found_controls.length - 10} more`}
                    </p>
                  </div>
                )}
              </div>
            )}

            {result && result.error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex items-start gap-2">
                  <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-red-900">Import Failed</h4>
                    <p className="text-sm text-red-700 mt-1">{result.message}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={uploading}
            >
              {result && !result.error ? 'Close' : 'Cancel'}
            </Button>
            {(!result || result.error) && (
              <Button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="bg-slate-900 text-white hover:bg-slate-800"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
